/**
 * ShareAuditButton — generates a 1080×1080 PNG of the audit insights and
 * lets the user download / copy / share it (T3 viralité).
 *
 * Renders the off-screen ShareableAuditCard, then html2canvas snapshots it
 * and produces a Blob. UX :
 *   - "Générer ma carte" → spinner ~1-2s → preview thumbnail + 3 actions :
 *     [Télécharger PNG] [Copier image] [Partager via OS share sheet]
 *
 * Privacy : option "Anonymiser le prénom" (J. D. au lieu de Jean Dupont).
 */
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Download, Loader2, CheckCircle2, X, Eye, EyeOff } from 'lucide-react';
import html2canvas from 'html2canvas';
import {
  ShareableAuditCard,
  type ShareableAuditCardData,
} from '@/components/ShareableAuditCard';

interface Props {
  data: Omit<ShareableAuditCardData, 'anonymise'>;
}

export function ShareAuditButton({ data }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="mb-3 flex w-full items-center justify-between gap-3 rounded-2xl border border-pink/30 bg-gradient-to-br from-pink/5 to-violet/5 px-4 py-3.5 text-left transition hover:from-pink/10 hover:to-violet/10"
      >
        <span className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-violet-500 text-white">
            <Share2 size={15} />
          </span>
          <span>
            <span className="font-display text-[14px] font-800 tracking-[-0.015em]">
              {t('share.cta')}
            </span>
            <p className="mt-0.5 text-[12px] text-ink/55">
              {t('share.ctaSub')}
            </p>
          </span>
        </span>
        <span className="font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-pink">
          NEW
        </span>
      </button>
      {open && <ShareModal data={data} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareModal({
  data,
  onClose,
}: {
  data: Omit<ShareableAuditCardData, 'anonymise'>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [anonymise, setAnonymise] = useState(true);
  const [isRendering, setIsRendering] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [didCopy, setDidCopy] = useState(false);

  const generate = useCallback(async () => {
    if (!cardRef.current) return;
    setIsRendering(true);
    setBlobUrl(null);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 1,
        useCORS: true,
        logging: false,
      });
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setIsRendering(false);
      }, 'image/png');
    } catch (err) {
      console.error('html2canvas failed:', err);
      setIsRendering(false);
    }
  }, []);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `audit-${data.prospect_name.toLowerCase().replace(/\s+/g, '-')}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copy = async () => {
    if (!blobUrl) return;
    try {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setDidCopy(true);
      setTimeout(() => setDidCopy(false), 2000);
    } catch (err) {
      console.error('clipboard write failed:', err);
    }
  };

  const share = async () => {
    if (!blobUrl) return;
    try {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      const file = new File([blob], `audit-${data.prospect_name}.png`, {
        type: 'image/png',
      });
      const shareData: ShareData = {
        title: `Audit ${data.prospect_name} — Inea`,
        text: `Voici l'analyse Inea de ${data.prospect_name} — Network-iA.io`,
        files: [file],
      };
      if (
        typeof navigator.canShare === 'function' &&
        navigator.canShare(shareData)
      ) {
        await navigator.share(shareData);
      } else {
        download();
      }
    } catch {
      // user cancelled — silent
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 px-4">
      <div className="card w-full max-w-2xl space-y-4 p-5">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-700 text-ink">
              {t('share.modalTitle', { name: data.prospect_name })}
            </h3>
            <p className="mt-0.5 text-[12px] text-ink/55">
              {t('share.modalSub')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-ink/60 hover:bg-ink/5"
          >
            <X size={16} />
          </button>
        </header>

        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-ink/10 bg-white px-3 py-2">
          <span className="flex items-center gap-2 text-sm font-700 text-ink/80">
            {anonymise ? <EyeOff size={14} /> : <Eye size={14} />}
            {t('share.anonymise')}
          </span>
          <input
            type="checkbox"
            checked={anonymise}
            onChange={(e) => {
              setAnonymise(e.target.checked);
              setBlobUrl(null);
            }}
            className="h-5 w-5 cursor-pointer accent-violet-500"
          />
        </label>

        {!blobUrl ? (
          <button
            onClick={generate}
            disabled={isRendering}
            className="hero-cta-light-primary inline-flex w-full items-center justify-center gap-2 disabled:opacity-50"
          >
            {isRendering ? (
              <>
                <Loader2 size={14} className="animate-spin" /> {t('share.generating')}
              </>
            ) : (
              <>
                <Share2 size={14} /> {t('share.generate')}
              </>
            )}
          </button>
        ) : (
          <>
            <img
              src={blobUrl}
              alt={t('share.cardAlt')}
              className="w-full rounded-2xl border border-ink/10 shadow-lg"
            />
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={download}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-[12px] font-700 hover:bg-ink/5"
              >
                <Download size={13} /> PNG
              </button>
              <button
                onClick={copy}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-[12px] font-700 hover:bg-ink/5"
              >
                {didCopy ? (
                  <>
                    <CheckCircle2 size={13} className="text-green-600" /> {t('share.copied')}
                  </>
                ) : (
                  <>📋 {t('share.copyImage')}</>
                )}
              </button>
              <button
                onClick={share}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-ink px-3 py-2.5 text-[12px] font-700 text-white hover:bg-ink/90"
              >
                <Share2 size={13} /> {t('share.shareBtn')}
              </button>
            </div>
            <p className="text-center text-[11px] text-ink/55">
              {t('share.tip')}
            </p>
          </>
        )}
      </div>

      {/* Off-screen render — html2canvas snapshots this */}
      <div
        style={{
          position: 'fixed',
          left: -10000,
          top: 0,
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      >
        <ShareableAuditCard ref={cardRef} {...data} anonymise={anonymise} />
      </div>
    </div>
  );
}
